import React, { useState, useRef, useEffect } from 'react';
import { useClusterAudit } from './useClusterAudit';
//import * as XLSX from 'xlsx';

export default function ClusterHealth() {
  const {
    allCustomers, EXPECTED_TABS, selectedTabs, setSelectedTabs,
    availableClusters, selectedCustomer, 
    tabData, activeTab, setActiveTab, isInitialLoading, lastSynced, 
    handleNameChange, handleClusterChange, runFullAudit
  } = useClusterAudit();

  const [query, setQuery] = useState(() => sessionStorage.getItem('cluster_health_query') || "");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showTabSelector, setShowTabSelector] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem('cluster_health_query', query);
  }, [query]);

  const toggleTabSelection = (tab) => {
    let newTabs;
    if (selectedTabs.includes(tab)) {
      if (selectedTabs.length > 1) {
        newTabs = selectedTabs.filter(t => t !== tab);
      } else {
        return;
      }
    } else {
      newTabs = [...selectedTabs, tab];
    }
    setSelectedTabs(newTabs); 
  };

  const handleKeyDown = (e) => {
    const filtered = allCustomers.filter(c => c.name.toLowerCase().includes(query.toLowerCase()));
    if (e.keyCode === 40 && activeSuggestionIndex < filtered.length - 1) {
      setActiveSuggestionIndex(activeSuggestionIndex + 1);
    } else if (e.keyCode === 38 && activeSuggestionIndex > 0) {
      setActiveSuggestionIndex(activeSuggestionIndex - 1);
    } else if (e.keyCode === 13 && activeSuggestionIndex > -1) {
      const selection = filtered[activeSuggestionIndex].name;
      selectCustomer(selection);
    }
  };

  const selectCustomer = (name) => {
    setQuery(name);
    handleNameChange(name);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
  };

  const formatKey = (key) => key ? key.replace(/([A-Z])/g, ' $1').trim().toUpperCase() : "";

  const extractNumber = (str) => {
    if (typeof str !== 'string') return parseFloat(str) || 0;
    return parseFloat(str.replace(/[^0-9.]/g, '')) || 0;
  };

  const getStatusBannerClass = () => {
    const status = tabData["clusterDetails"]?.["healthStatus"]?.observed?.toLowerCase();
    if (status === 'healthy' || status === 'health_ok') return 'banner-success';
    if (status === 'unhealthy' || status === 'error') return 'banner-danger';
    return 'banner-pending';
  };

  const getPillClass = (val, key) => {
    const k = key.toLowerCase();
    const v = String(val).toLowerCase();
    if (k.includes('container') || k.includes('pod')) {
        const hasRestarts = /RestartCount:\s*([1-9]\d*)/.test(v);
        const isBadState = v.includes("terminated") || v.includes("waiting") || v.includes("failed");
        return (hasRestarts || isBadState) ? "pill-danger" : "pill-success";
    }
    if (k.includes('status') || k.includes('health') || k.includes('state')) {
      if (['healthy', 'running', 'active', 'online', 'health_ok', 'success', 'succeeded', 'yes'].includes(v)) return "pill-success";
      if (['unhealthy', 'offline', 'error', 'danger', 'failed', 'no', 'stopped'].includes(v)) return "pill-danger";
      return "pill-warning";
    }
    const num = extractNumber(v);
    if (k.includes('cores')) return num >= 32 ? "pill-success" : num >= 16 ? "pill-warning" : "pill-danger";
    if (k.includes('memoryusage')) return num < 60 ? "pill-success" : num >= 85 ? "pill-danger" : "pill-warning";
    if (k.includes('memory')) return num >= 64 ? "pill-success" : num >= 32 ? "pill-warning" : "pill-danger";
    if (k.includes('restartcount')) return num === 0 ? "pill-success" : num < 5 ? "pill-warning" : "pill-danger";
    return "pill-default";
  };

  const COLUMN_HEADERS = {
    "clusterDetails": { col1: "Property", col2: "Observed Value", col3: "Recommended Value" },
    "rapidPods": { col1: "Pod Name", col2: "Current state", col3: "Attached Containers" },
    "default": { col1: "Property", col2: "Observed", col3: "Recommended" }
  };

  return (
    <div className="audit-content">
      <div className="card">
        <h2><center>Rapid Cluster Health Monitor</center></h2>
        
        <div className="selection-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="input-row" style={{ display: 'flex', gap: '15px', width: '100%' }}>
            
            <div className="autocomplete-wrapper" ref={autocompleteRef} style={{ flex: '2', position: 'relative' }}>
              <input 
                type="text" className="input-field" placeholder="Search Customer..." 
                autoComplete="off" value={query} onKeyDown={handleKeyDown}
                onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
                style={{ width: '100%', height: '45px', boxSizing: 'border-box' }} 
              /> 
              {showSuggestions && query && (
                <div className="autocomplete-suggestions" style={{ width: '100%', position: 'absolute', zIndex: 100, background: 'white', border: '1px solid #cbd5e1' }}>
                  {allCustomers.filter(c => c.name.toLowerCase().includes(query.toLowerCase())).slice(0, 8).map((match, i) => (
                    <div key={i} onClick={() => selectCustomer(match.name)} style={{ padding: '10px', background: i === activeSuggestionIndex ? '#f1f5f9' : 'transparent', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}>
                      {match.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <select className="dropdown-field" onChange={handleClusterChange} value={selectedCustomer?.clusterName || ""} style={{ flex: '1', height: '45px' }}>
              <option value="" disabled>Select Cluster</option>
              {availableClusters.map((c, i) => (<option key={i} value={c.clusterName}>{c.clusterName}</option>))}
            </select>

            <div className="tab-selector-wrapper" style={{ flex: '1', position: 'relative' }}>
              <div className="dropdown-field" onClick={() => setShowTabSelector(!showTabSelector)} style={{ height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', padding: '0 15px', cursor: 'pointer', border: '1px solid #cbd5e1', borderRadius: '4px' }}>
                <span style={{fontSize: '0.85rem', fontWeight: '600'}}>{selectedTabs.length} Tabs Selected</span>
                <span>{showTabSelector ? '▴' : '▾'}</span>
              </div>
              {showTabSelector && (
                <div style={{ position: 'absolute', top: '50px', right: 0, width: '220px', background: 'white', border: '1px solid #cbd5e1', zIndex: 9999, padding: '12px', borderRadius: '8px', boxShadow: '0 10px 15px rgba(0,0,0,0.1)' }}>
                  {EXPECTED_TABS.map(tab => (
                    <label key={tab} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', cursor: 'pointer' }}>
                      <input className="custom-input-box" type="checkbox" checked={selectedTabs.includes(tab)} onChange={() => toggleTabSelection(tab)} />
                      <span className="custom-input-box" style={{fontSize: '0.85rem'}}>{formatKey(tab)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button className="primary-btn go-btn-full" onClick={runFullAudit} disabled={isInitialLoading || !selectedCustomer} style={{ width: '100%', height: '50px', fontSize: '1.1rem', fontWeight: '800' }}>
            {isInitialLoading ? "SYNCING CLUSTER DATA..." : "RUN HEALTH AUDIT"}
          </button>
        </div>
      </div>

      {(isInitialLoading || (lastSynced && Object.keys(tabData).length > 0)) && (
        <div className="result-container" style={{ marginTop: '30px' }}>
          <div className={`status-banner ${getStatusBannerClass()}`} style={{ display: 'flex', alignItems: 'center', padding: '12px 20px', borderRadius: '8px 8px 0 0', color: 'white' }}>
            <div className="banner-section center" style={{ flex: 1, fontWeight: '700' }}>
              {isInitialLoading ? "SYNCING..." : `SYSTEM STATUS: ${(tabData["clusterDetails"]?.["healthStatus"]?.observed || "HEALTHY").toUpperCase()} | SYNCED: ${lastSynced}`}
            </div>
          </div>

          <div className="tab-nav" style={{ display: 'flex', gap: '15px', marginTop: '25px', flexWrap: 'wrap' }}>
            {/* RESTORED: Full Tab Wrapper logic including Source Pills */}
            {EXPECTED_TABS.filter(tab => selectedTabs.includes(tab)).map(tab => (
              <div key={tab} className="tab-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1' }}>
                <button 
                  className={`tab-btn ${activeTab === tab ? 'active' : ''}`} 
                  onClick={() => setActiveTab(tab)}
                  style={{ width: '100%', marginBottom: '8px', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', cursor: 'pointer', fontWeight: '600' }}
                >
                  {tabData[tab] ? "✅ " : "⏳ "}{formatKey(tab)}
                </button>
                
                <span className="source-pill" style={{
                  fontSize: '9px', fontWeight: '800', padding: '2px 10px', borderRadius: '12px',
                  backgroundColor: '#e2e8f0', color: '#475569', border: '1px solid #cbd5e1',
                  textTransform: 'uppercase', letterSpacing: '0.5px'
                }}>
                  {tab === "clusterDetails" ? "SPECTRO CLOUD" : "KUBECONFIG"}
                </span>
              </div>
            ))}
          </div>

          <table className="health-table" style={{ width: '100%', marginTop: '20px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', backgroundColor: '#f8fafc' }}>
                <th style={{ padding: '12px', borderBottom: '2px solid #e2e8f0' }}>{(COLUMN_HEADERS[activeTab] || COLUMN_HEADERS.default).col1}</th>
                <th style={{ padding: '12px', borderBottom: '2px solid #e2e8f0' }}>{(COLUMN_HEADERS[activeTab] || COLUMN_HEADERS.default).col2}</th>
                <th style={{ padding: '12px', borderBottom: '2px solid #e2e8f0' }}>{(COLUMN_HEADERS[activeTab] || COLUMN_HEADERS.default).col3}</th>
              </tr>
            </thead>
            <tbody>
              {tabData[activeTab] ? (
                Object.entries(tabData[activeTab]).map(([key, audit]) => (
                  <tr key={key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td className="td-label" style={{ padding: '12px', fontWeight: '600' }}>{formatKey(key)}</td>
                    <td className="td-value" style={{ padding: '12px' }}>
                      <span className={`status-pill ${getPillClass(audit?.observed, key)}`}>
                        {String(audit.observed).split(',').map((item, i) => <div key={i}>{item.trim()}</div>)}
                      </span>
                    </td>
                    <td className="td-value recommended-col" style={{ padding: '12px', color: '#64748b' }}>
                      {String(audit.recommended).split(',').map((item, i) => <div key={i}>{item.trim()}</div>)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
                    {isInitialLoading ? "Data for this section is being retrieved..." : "No data available."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}